/**
 * GraphMobileView Component
 * Mobile-friendly alternative to the interactive graph
 * Shows filtered organization list when filters are active, otherwise most connected
 *
 * Carries its own GraphExplainer (#27, `context="list"`) next to the
 * "Organizations" list heading: EcosystemGraph's canvas-based explainer
 * doesn't reach mobile visitors at all (this component replaces the canvas
 * entirely below the 992px breakpoint), and mobile-first for rural/
 * under-resourced users is exactly this codebase's own accessibility
 * pillar — the first-time-visitor persona the ticket describes is at least
 * as likely to be on a phone as a desktop.
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
    Card,
    ListGroup,
    Badge,
    Spinner,
    Alert,
    Button,
} from 'react-bootstrap';

import { useOrganizationApi } from '@/shared/services/organizationApi';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';

import { useGraphStats, useMostConnected } from '../hooks/useGraphData';

import GraphExplainer from './GraphExplainer';

export default function GraphMobileView({ filters = {} }) {
    const { ecosystem } = useEcosystem();
    const ecosystemName = ecosystem?.name || 'Ecosystem Network';
    const { stats, loading: statsLoading, error: statsError } = useGraphStats();
    const {
        organizations: mostConnected,
        loading: mostConnectedLoading,
        error: mostConnectedError,
    } = useMostConnected(10);

    // State for filtered results
    const [filteredOrgs, setFilteredOrgs] = useState([]);
    const [filteredLoading, setFilteredLoading] = useState(false);
    const [filteredError, setFilteredError] = useState(null);
    const [showingMore, setShowingMore] = useState(false);

    const orgApi = useOrganizationApi();
    const orgApiRef = useRef(orgApi);
    orgApiRef.current = orgApi;

    // Check if any filters are active
    const hasActiveFilters = !!(
        filters.searchTerm ||
        filters.sector ||
        filters.region ||
        filters.programming ||
        filters.hasWebsite ||
        filters.hasDescription ||
        filters.minConnections
    );

    // Fetch filtered organizations when filters are active
    useEffect(() => {
        if (!hasActiveFilters) {
            setFilteredOrgs([]);
            return;
        }

        const fetchFiltered = async () => {
            setFilteredLoading(true);
            setFilteredError(null);

            try {
                const params = {
                    page: 1,
                    limit: showingMore ? 20 : 10,
                    sortBy: 'connectionCount',
                    sortOrder: 'desc',
                };

                if (filters.sector) params.sector = filters.sector;
                if (filters.region) params.region = filters.region;
                if (filters.hasWebsite) params.hasWebsite = filters.hasWebsite;
                if (filters.hasDescription) {
                    params.hasDescription = filters.hasDescription;
                }
                if (filters.minConnections) {
                    params.minConnections = filters.minConnections;
                }

                let data;
                if (filters.searchTerm && filters.searchTerm.length >= 2) {
                    data = await orgApiRef.current.searchOrganizations(
                        filters.searchTerm,
                        params
                    );
                } else {
                    data = await orgApiRef.current.fetchOrganizations(params);
                }

                setFilteredOrgs(data.organizations || []);
            } catch (err) {
                console.error('Failed to fetch filtered organizations:', err);
                setFilteredError('Failed to load filtered results');
            } finally {
                setFilteredLoading(false);
            }
        };

        fetchFiltered();
    }, [
        hasActiveFilters,
        filters.searchTerm,
        filters.sector,
        filters.region,
        filters.programming,
        filters.hasWebsite,
        filters.hasDescription,
        filters.minConnections,
        showingMore,
    ]);

    const loading =
        statsLoading ||
        (hasActiveFilters ? filteredLoading : mostConnectedLoading);
    const error =
        statsError || (hasActiveFilters ? filteredError : mostConnectedError);

    // Determine which organizations to show
    const displayOrgs = hasActiveFilters ? filteredOrgs : mostConnected;

    if (loading && displayOrgs.length === 0) {
        return (
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-4 text-center">
                    <Spinner animation="border" variant="primary" size="sm" />
                    <p className="mt-2 mb-0 small text-muted">
                        Loading network data...
                    </p>
                </Card.Body>
            </Card>
        );
    }

    if (error) {
        return (
            <Alert variant="warning" className="mb-0">
                <small>{error}</small>
            </Alert>
        );
    }

    return (
        <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
                {/* Network Stats */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="text-primary mb-0">{ecosystemName}</h5>
                    <GraphExplainer
                        variant="outline-secondary"
                        context="list"
                    />
                </div>

                <div className="d-flex justify-content-around text-center mb-4">
                    <div>
                        <div className="h3 text-primary mb-0">
                            {stats?.nodeCount || 0}
                        </div>
                        <small className="text-muted">Organizations</small>
                    </div>
                    <div>
                        <div className="h3 text-primary mb-0">
                            {stats?.edgeCount || 0}
                        </div>
                        <small className="text-muted">Connections</small>
                    </div>
                    <div>
                        <div className="h3 text-primary mb-0">
                            {stats?.avgDegree || 0}
                        </div>
                        <small className="text-muted">Avg. Links</small>
                    </div>
                </div>

                {/* Organizations List */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="text-muted mb-0">
                        {hasActiveFilters
                            ? `Filtered Results (${filteredOrgs.length})`
                            : 'Most Connected Organizations'}
                    </h6>
                    {filteredLoading && (
                        <Spinner
                            animation="border"
                            variant="primary"
                            size="sm"
                        />
                    )}
                </div>

                {hasActiveFilters &&
                filteredOrgs.length === 0 &&
                !filteredLoading ? (
                    <div className="text-center py-4 text-muted">
                        <p className="mb-0">
                            No organizations match your filters.
                        </p>
                        <small>Try adjusting your search criteria.</small>
                    </div>
                ) : (
                    <ListGroup variant="flush">
                        {displayOrgs.map((item, index) => {
                            // Handle both filtered (org object) and mostConnected (nested) formats
                            const org = item.organization || item;
                            const orgId = org._id || org.id;
                            const connectionCount =
                                item.connectionCount ||
                                org.connectionCount ||
                                0;

                            return (
                                <ListGroup.Item
                                    key={orgId}
                                    as={Link}
                                    to={`/organizations/${orgId}`}
                                    className="d-flex justify-content-between align-items-center px-0 py-2 text-decoration-none"
                                    action
                                >
                                    <div className="d-flex align-items-center">
                                        <span className="text-muted me-2 small">
                                            {index + 1}.
                                        </span>
                                        <div>
                                            <span className="text-dark">
                                                {org.name}
                                            </span>
                                            {org.primarySector && (
                                                <small className="d-block text-muted">
                                                    {org.primarySector}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                    {connectionCount > 0 && (
                                        <Badge bg="light" text="dark" pill>
                                            {connectionCount}
                                        </Badge>
                                    )}
                                </ListGroup.Item>
                            );
                        })}
                    </ListGroup>
                )}

                {/* Show More button for filtered results */}
                {hasActiveFilters &&
                    filteredOrgs.length >= 10 &&
                    !showingMore && (
                        <div className="text-center mt-3">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setShowingMore(true)}
                            >
                                Show More
                            </Button>
                        </div>
                    )}

                {/* Desktop notice */}
                <div className="mt-4 p-3 bg-light rounded text-center">
                    <small className="text-muted">
                        View the full interactive network visualization on a
                        larger screen for the best experience.
                    </small>
                </div>
            </Card.Body>
        </Card>
    );
}

GraphMobileView.propTypes = {
    filters: PropTypes.shape({
        searchTerm: PropTypes.string,
        sector: PropTypes.string,
        region: PropTypes.string,
        programming: PropTypes.string,
        hasWebsite: PropTypes.string,
        hasDescription: PropTypes.string,
        minConnections: PropTypes.string,
    }),
};

GraphMobileView.defaultProps = {
    filters: {},
};
